import React, { useState, useEffect, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { 
  Calendar, Plus, Send, Clock, Users, Filter, Repeat, 
  CheckCircle2, XCircle, Loader2, ChevronRight, Copy,
  Play, Pause, Trash2, Edit2, Eye, BarChart3, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { broadcastService, respondioService, portfolioService, campaignService } from '@/lib/api';

const BroadcastManager = () => {
  const [activeView, setActiveView] = useState('calendar');
  const [broadcasts, setBroadcasts] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaryStats, setSummaryStats] = useState(null);
  
  // Modal states
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  
  // Form data
  const [templates, setTemplates] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [respondioStatus, setRespondioStatus] = useState(null);
  
  // Preview data
  const [previewData, setPreviewData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  
  // New broadcast form
  const [formData, setFormData] = useState({
    name: '',
    template_name: '',
    language_code: 'es_AR',
    parameter_mapping: {},
    scheduled_datetime: '',
    recurrence_type: 'none',
    recurrence_end_date: '',
    filter_criteria: {
      portfolio_ids: [],
      campaign_ids: [],
      exclude_paid: true,
      exclude_partial_paid: false,
      min_debt: null,
      max_debt: null,
      tags: []
    }
  });
  
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Available fields for mapping
  const availableFields = [
    { value: 'name', label: 'Nombre' },
    { value: 'dni', label: 'DNI' },
    { value: 'cuit', label: 'CUIT' },
    { value: 'client_code', label: 'Código Cliente' },
    { value: 'total_debt', label: 'Deuda Total' },
    { value: 'portfolio_name', label: 'Cartera' },
    { value: 'phone', label: 'Teléfono' },
    { value: 'email', label: 'Email' },
    { value: 'address', label: 'Dirección' },
    { value: 'link', label: 'Link de Pago' },
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [broadcastsRes, statusRes, portfoliosRes, campaignsRes] = await Promise.all([
        broadcastService.list(),
        respondioService.getStatus(),
        portfolioService.list(),
        campaignService.list()
      ]);
      
      setBroadcasts(broadcastsRes.data || []);
      setRespondioStatus(statusRes.data);
      setPortfolios(portfoliosRes.data || []);
      setCampaigns(campaignsRes.data || []);
      
      if (statusRes.data?.connected) {
        const templatesRes = await respondioService.getTemplates();
        setTemplates(templatesRes.data || []);
      }
      
      // Load summary stats
      const statsRes = await broadcastService.getSummaryStats(30);
      setSummaryStats(statsRes.data);
      
      // Load calendar events for current month
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const endDate = new Date(now.getFullYear(), now.getMonth() + 2, 0).toISOString();
      const eventsRes = await broadcastService.getCalendarEvents(startDate, endDate);
      setCalendarEvents(eventsRes.data || []);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = (info) => {
    const clickedDate = new Date(info.date);
    clickedDate.setHours(9, 0, 0, 0);
    setFormData({
      ...formData,
      scheduled_datetime: clickedDate.toISOString().slice(0, 16)
    });
    setShowCreate(true);
  };

  const handleEventClick = (info) => {
    const broadcastId = info.event.id.split('-')[0];
    const broadcast = broadcasts.find(b => b.id === broadcastId);
    if (broadcast) {
      setSelectedBroadcast(broadcast);
      setShowEdit(true);
    }
  };

  const handleSelectTemplate = (templateName) => {
    const template = templates.find(t => t.name === templateName);
    setSelectedTemplate(template);
    
    if (template) {
      const mapping = {};
      template.variables?.forEach((v) => {
        mapping[v] = '';
      });
      setFormData({ ...formData, template_name: templateName, parameter_mapping: mapping });
    }
  };

  const handleCreateBroadcast = async () => {
    if (!formData.name || !formData.template_name || !formData.scheduled_datetime) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        scheduled_datetime: new Date(formData.scheduled_datetime).toISOString(),
        recurrence_end_date: formData.recurrence_end_date 
          ? new Date(formData.recurrence_end_date).toISOString() 
          : null
      };
      
      await broadcastService.create(payload);
      toast.success('Difusión programada creada');
      setShowCreate(false);
      resetForm();
      loadInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear difusión');
    }
  };

  const handleUpdateBroadcast = async () => {
    if (!selectedBroadcast) return;
    
    try {
      const payload = {
        name: formData.name,
        template_name: formData.template_name,
        parameter_mapping: formData.parameter_mapping,
        scheduled_datetime: formData.scheduled_datetime 
          ? new Date(formData.scheduled_datetime).toISOString() 
          : undefined,
        recurrence_type: formData.recurrence_type,
        recurrence_end_date: formData.recurrence_end_date 
          ? new Date(formData.recurrence_end_date).toISOString() 
          : null,
        filter_criteria: formData.filter_criteria
      };
      
      await broadcastService.update(selectedBroadcast.id, payload);
      toast.success('Difusión actualizada');
      setShowEdit(false);
      setSelectedBroadcast(null);
      loadInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDeleteBroadcast = async (broadcastId) => {
    if (!window.confirm('¿Cancelar esta difusión programada?')) return;
    
    try {
      await broadcastService.delete(broadcastId);
      toast.success('Difusión cancelada');
      setShowEdit(false);
      loadInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    }
  };

  const handleDuplicate = async (broadcastId) => {
    try {
      await broadcastService.duplicate(broadcastId);
      toast.success('Difusión duplicada');
      loadInitialData();
    } catch (error) {
      toast.error('Error al duplicar');
    }
  };

  const handleExecuteNow = async (broadcastId) => {
    if (!window.confirm('¿Ejecutar esta difusión ahora?')) return;
    
    try {
      const result = await broadcastService.executeNow(broadcastId);
      toast.success(`Enviados: ${result.data.sent}, Fallidos: ${result.data.failed}`);
      loadInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ejecutar');
    }
  };

  const handlePreview = async (broadcast) => {
    setSelectedBroadcast(broadcast);
    try {
      const result = await broadcastService.preview(broadcast.id, 20);
      setPreviewData(result.data);
      setShowPreview(true);
    } catch (error) {
      toast.error('Error al cargar preview');
    }
  };

  const handleViewStats = async (broadcast) => {
    setSelectedBroadcast(broadcast);
    try {
      const result = await broadcastService.getStats(broadcast.id);
      setStatsData(result.data);
      setShowStats(true);
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      template_name: '',
      language_code: 'es_AR',
      parameter_mapping: {},
      scheduled_datetime: '',
      recurrence_type: 'none',
      recurrence_end_date: '',
      filter_criteria: {
        portfolio_ids: [],
        campaign_ids: [],
        exclude_paid: true,
        exclude_partial_paid: false,
        min_debt: null,
        max_debt: null,
        tags: []
      }
    });
    setSelectedTemplate(null);
  };

  const openEditModal = (broadcast) => {
    setSelectedBroadcast(broadcast);
    setFormData({
      name: broadcast.name,
      template_name: broadcast.template_name,
      language_code: broadcast.language_code || 'es_AR',
      parameter_mapping: broadcast.parameter_mapping || {},
      scheduled_datetime: broadcast.scheduled_datetime?.slice(0, 16) || '',
      recurrence_type: broadcast.recurrence_type || 'none',
      recurrence_end_date: broadcast.recurrence_end_date?.slice(0, 16) || '',
      filter_criteria: broadcast.filter_criteria || {}
    });
    const template = templates.find(t => t.name === broadcast.template_name);
    setSelectedTemplate(template);
    setShowEdit(true);
  };

  const formatCalendarEvents = useCallback(() => {
    return calendarEvents.map(event => ({
      id: event.id,
      title: event.title,
      start: event.start,
      backgroundColor: event.status === 'completed' ? '#10b981' : 
                       event.status === 'pending' ? '#3b82f6' : '#f59e0b',
      borderColor: 'transparent',
      extendedProps: {
        recipients: event.recipients,
        status: event.status,
        type: event.type
      }
    }));
  }, [calendarEvents]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!respondioStatus?.connected) {
    return (
      <Card className="p-8 text-center">
        <XCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-900 mb-2">Respond.io no configurado</h3>
        <p className="text-slate-600">
          Contacta al SuperAdmin para configurar las credenciales de Respond.io y poder programar difusiones.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con stats */}
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Difusiones Programadas</h2>
          <p className="text-slate-600">Programa y gestiona envíos masivos de WhatsApp</p>
        </div>
        
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => loadInitialData()}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Button
            className="bg-teal-600 hover:bg-teal-700"
            onClick={() => {
              resetForm();
              setShowCreate(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Difusión
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {summaryStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Pendientes</p>
                <p className="text-2xl font-bold text-blue-600">{summaryStats.pending_broadcasts}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-200" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Enviados (30d)</p>
                <p className="text-2xl font-bold text-emerald-600">{summaryStats.messages?.sent || 0}</p>
              </div>
              <Send className="w-8 h-8 text-emerald-200" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Tasa Entrega</p>
                <p className="text-2xl font-bold text-teal-600">{summaryStats.rates?.delivery_rate || '0%'}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-teal-200" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Tasa Lectura</p>
                <p className="text-2xl font-bold text-violet-600">{summaryStats.rates?.read_rate || '0%'}</p>
              </div>
              <Eye className="w-8 h-8 text-violet-200" />
            </div>
          </Card>
        </div>
      )}

      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView}>
        <TabsList>
          <TabsTrigger value="calendar">
            <Calendar className="w-4 h-4 mr-2" />
            Calendario
          </TabsTrigger>
          <TabsTrigger value="list">
            <Clock className="w-4 h-4 mr-2" />
            Lista
          </TabsTrigger>
        </TabsList>

        {/* Calendar View */}
        <TabsContent value="calendar">
          <Card className="p-4">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              locale="es"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek'
              }}
              events={formatCalendarEvents()}
              dateClick={handleDateClick}
              eventClick={handleEventClick}
              height="auto"
              buttonText={{
                today: 'Hoy',
                month: 'Mes',
                week: 'Semana'
              }}
            />
          </Card>
        </TabsContent>

        {/* List View */}
        <TabsContent value="list">
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Nombre</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Template</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Programada</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Recurrencia</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Destinatarios</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {broadcasts.map((broadcast) => (
                    <tr key={broadcast.id} className="hover:bg-slate-50">
                      <td className="px-4 py-4">
                        <p className="font-medium text-slate-900">{broadcast.name}</p>
                      </td>
                      <td className="px-4 py-4">
                        <Badge variant="outline">{broadcast.template_name}</Badge>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {broadcast.scheduled_datetime ? (
                          new Date(broadcast.scheduled_datetime).toLocaleString('es-AR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        ) : '-'}
                      </td>
                      <td className="px-4 py-4">
                        <Badge className={
                          broadcast.recurrence_type === 'none' ? 'bg-slate-100 text-slate-700' :
                          broadcast.recurrence_type === 'daily' ? 'bg-blue-100 text-blue-700' :
                          broadcast.recurrence_type === 'weekly' ? 'bg-purple-100 text-purple-700' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {broadcast.recurrence_type === 'none' ? 'Única' :
                           broadcast.recurrence_type === 'daily' ? 'Diaria' :
                           broadcast.recurrence_type === 'weekly' ? 'Semanal' : 'Mensual'}
                        </Badge>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-slate-900 font-medium">{broadcast.estimated_recipients || 0}</span>
                      </td>
                      <td className="px-4 py-4">
                        <Badge className={
                          broadcast.status === 'pending' ? 'bg-blue-100 text-blue-700' :
                          broadcast.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          broadcast.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {broadcast.status === 'pending' ? 'Pendiente' :
                           broadcast.status === 'completed' ? 'Completada' :
                           broadcast.status === 'cancelled' ? 'Cancelada' : 'Pausada'}
                        </Badge>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          {broadcast.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditModal(broadcast)}
                                title="Editar"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleExecuteNow(broadcast.id)}
                                title="Ejecutar ahora"
                                className="text-emerald-600"
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteBroadcast(broadcast.id)}
                                title="Cancelar"
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handlePreview(broadcast)}
                            title="Ver destinatarios"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDuplicate(broadcast.id)}
                            title="Duplicar"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          {broadcast.executions?.length > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewStats(broadcast)}
                              title="Ver estadísticas"
                              className="text-violet-600"
                            >
                              <BarChart3 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {broadcasts.length === 0 && (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No hay difusiones programadas</p>
                  <Button
                    className="mt-4"
                    onClick={() => {
                      resetForm();
                      setShowCreate(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Crear primera difusión
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Modal */}
      <Dialog open={showCreate || showEdit} onOpenChange={(open) => {
        if (!open) {
          setShowCreate(false);
          setShowEdit(false);
          setSelectedBroadcast(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {showEdit ? 'Editar Difusión' : 'Nueva Difusión Programada'}
            </DialogTitle>
            <DialogDescription>
              Configura los detalles del envío masivo de WhatsApp
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nombre de la difusión *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Recordatorio de pago - Marzo"
                />
              </div>
              
              <div>
                <Label>Fecha y hora *</Label>
                <Input
                  type="datetime-local"
                  value={formData.scheduled_datetime}
                  onChange={(e) => setFormData({ ...formData, scheduled_datetime: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Recurrencia</Label>
                <Select
                  value={formData.recurrence_type}
                  onValueChange={(v) => setFormData({ ...formData, recurrence_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Única vez</SelectItem>
                    <SelectItem value="daily">Diaria</SelectItem>
                    <SelectItem value="weekly">Semanal</SelectItem>
                    <SelectItem value="monthly">Mensual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.recurrence_type !== 'none' && (
                <div className="col-span-2">
                  <Label>Fecha fin de recurrencia</Label>
                  <Input
                    type="datetime-local"
                    value={formData.recurrence_end_date}
                    onChange={(e) => setFormData({ ...formData, recurrence_end_date: e.target.value })}
                  />
                </div>
              )}
            </div>

            {/* Template Selection */}
            <div className="space-y-4">
              <Label>Template de WhatsApp *</Label>
              <Select
                value={formData.template_name}
                onValueChange={handleSelectTemplate}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id || t.name} value={t.name}>
                      {t.name} ({t.variables_count || 0} variables)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedTemplate && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Vista previa:</p>
                  <p className="text-sm whitespace-pre-wrap">{selectedTemplate.body_text}</p>
                </div>
              )}
              
              {/* Parameter Mapping */}
              {selectedTemplate && selectedTemplate.variables?.length > 0 && (
                <div className="space-y-3">
                  <Label>Mapeo de variables</Label>
                  {selectedTemplate.variables.map((varNum) => (
                    <div key={varNum} className="flex items-center gap-3">
                      <div className="w-20 text-center">
                        <Badge variant="outline">{`{{${varNum}}}`}</Badge>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                      <Select
                        value={formData.parameter_mapping[varNum] || ''}
                        onValueChange={(v) => setFormData({
                          ...formData,
                          parameter_mapping: { ...formData.parameter_mapping, [varNum]: v }
                        })}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Selecciona campo" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableFields.map(f => (
                            <SelectItem key={f.value} value={f.value}>
                              {f.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Filters */}
            <div className="space-y-4 bg-slate-50 p-4 rounded-lg">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-slate-600" />
                <Label className="text-base">Filtros de destinatarios</Label>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Carteras</Label>
                  <Select
                    value={formData.filter_criteria.portfolio_ids?.join(',') || 'all'}
                    onValueChange={(v) => setFormData({
                      ...formData,
                      filter_criteria: {
                        ...formData.filter_criteria,
                        portfolio_ids: v === 'all' ? [] : v.split(',')
                      }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todas las carteras" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las carteras</SelectItem>
                      {portfolios.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Campañas</Label>
                  <Select
                    value={formData.filter_criteria.campaign_ids?.join(',') || 'all'}
                    onValueChange={(v) => setFormData({
                      ...formData,
                      filter_criteria: {
                        ...formData.filter_criteria,
                        campaign_ids: v === 'all' ? [] : v.split(',')
                      }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todas las campañas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las campañas</SelectItem>
                      {campaigns.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Deuda mínima ($)</Label>
                  <Input
                    type="number"
                    value={formData.filter_criteria.min_debt || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      filter_criteria: {
                        ...formData.filter_criteria,
                        min_debt: e.target.value ? parseFloat(e.target.value) : null
                      }
                    })}
                    placeholder="Sin mínimo"
                  />
                </div>
                
                <div>
                  <Label>Deuda máxima ($)</Label>
                  <Input
                    type="number"
                    value={formData.filter_criteria.max_debt || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      filter_criteria: {
                        ...formData.filter_criteria,
                        max_debt: e.target.value ? parseFloat(e.target.value) : null
                      }
                    })}
                    placeholder="Sin máximo"
                  />
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 pt-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="exclude_paid"
                    checked={formData.filter_criteria.exclude_paid}
                    onCheckedChange={(checked) => setFormData({
                      ...formData,
                      filter_criteria: { ...formData.filter_criteria, exclude_paid: checked }
                    })}
                  />
                  <label htmlFor="exclude_paid" className="text-sm">
                    Excluir clientes que ya pagaron
                  </label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="exclude_partial"
                    checked={formData.filter_criteria.exclude_partial_paid}
                    onCheckedChange={(checked) => setFormData({
                      ...formData,
                      filter_criteria: { ...formData.filter_criteria, exclude_partial_paid: checked }
                    })}
                  />
                  <label htmlFor="exclude_partial" className="text-sm">
                    Excluir clientes con pagos parciales
                  </label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreate(false);
                  setShowEdit(false);
                  setSelectedBroadcast(null);
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={showEdit ? handleUpdateBroadcast : handleCreateBroadcast}
                className="flex-1 bg-teal-600 hover:bg-teal-700"
              >
                {showEdit ? 'Guardar Cambios' : 'Programar Difusión'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Vista Previa de Destinatarios</DialogTitle>
            <DialogDescription>
              {previewData?.total || 0} clientes recibirán este mensaje
            </DialogDescription>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-4">
              <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Nombre</th>
                      <th className="px-3 py-2 text-left">Teléfono</th>
                      <th className="px-3 py-2 text-left">Deuda</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {previewData.preview?.map((d) => (
                      <tr key={d.phone || d.name}>
                        <td className="px-3 py-2">{d.name}</td>
                        <td className="px-3 py-2">{d.phone}</td>
                        <td className="px-3 py-2">${(d.total_debt || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {previewData.total > previewData.showing && (
                <p className="text-sm text-slate-500 text-center">
                  Mostrando {previewData.showing} de {previewData.total} destinatarios
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Stats Modal */}
      <Dialog open={showStats} onOpenChange={setShowStats}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-violet-600" />
              Estadísticas de Envío
            </DialogTitle>
            <DialogDescription>
              {selectedBroadcast?.name}
            </DialogDescription>
          </DialogHeader>
          
          {statsData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-600">{statsData.summary?.sent || 0}</p>
                  <p className="text-sm text-slate-600">Enviados</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{statsData.summary?.delivered || 0}</p>
                  <p className="text-sm text-slate-600">Entregados</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-3xl font-bold text-violet-600">{statsData.summary?.read || 0}</p>
                  <p className="text-sm text-slate-600">Leídos</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-3xl font-bold text-red-600">{statsData.summary?.failed || 0}</p>
                  <p className="text-sm text-slate-600">Fallidos</p>
                </Card>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between text-sm mb-2">
                  <span>Tasa de entrega</span>
                  <span className="font-medium">{statsData.summary?.delivery_rate}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Tasa de lectura</span>
                  <span className="font-medium">{statsData.summary?.read_rate}</span>
                </div>
              </div>
              
              {statsData.execution?.errors?.length > 0 && (
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-800 mb-2">Errores:</p>
                  <ul className="text-xs text-red-700 space-y-1 max-h-24 overflow-y-auto">
                    {statsData.execution.errors.slice(0, 5).map((err, idx) => (
                      <li key={err}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BroadcastManager;
